import { fetchProfile, patchProfile } from '../api/profileApi';
import { getSessionToken } from '../auth/sessionToken';
import {
  extractProfileDisplaySummary,
  type ProfileDisplaySummary,
} from '../utils/profileDisplaySummary';
import {
  isLiteRegisterPlaceholderFirstName,
  isLiteRegisterPlaceholderLastName,
} from '../utils/profileLitePlaceholders';
import { contactHintsFromAuthBody, persistProfileContactHints } from '../utils/profileContactStorage';
import { syncLocalBookingProfileFromSummary } from '../utils/profileBookingCompleteStorage';
import { cacheProfileAvatarFromServer, loadLocalProfileAvatarSrc } from '../utils/profileAvatarStorage';
import { fetchRemoteImageAsUploadDataUrl } from '../utils/profileAvatarUpload';
import {
  googleProfileFromCredential,
  pictureFromGoogleCredential,
  type GoogleIdTokenProfile,
} from './googleSignIn';

export function buildProfilePatchFromGoogle(
  summary: ProfileDisplaySummary,
  google: GoogleIdTokenProfile,
  phoneHint: string | null,
): Record<string, string> {
  const patch: Record<string, string> = {};

  if (
    google.firstName &&
    (!summary.firstName?.trim() ||
      isLiteRegisterPlaceholderFirstName(summary.firstName, google.email, {
        lastName: summary.lastName,
      }))
  ) {
    patch.first_name = google.firstName;
  }
  if (
    google.lastName &&
    (!summary.lastName?.trim() || isLiteRegisterPlaceholderLastName(summary.lastName))
  ) {
    patch.last_name = google.lastName;
  }
  if (!summary.email?.trim() && google.email) {
    patch.email = google.email;
  }
  if (!summary.phone?.trim() && phoneHint?.trim()) {
    patch.phone_number = phoneHint.trim();
  }

  return patch;
}

/**
 * After Google login, use the profile photo from the login API or the Google ID token.
 * Skips when the user already has a locally saved avatar (manual upload/crop).
 */
export function syncProfileAvatarAfterGoogleLogin(credential: string, loginBody: unknown): void {
  if (loadLocalProfileAvatarSrc()) {
    return;
  }

  const fromLoginApi = extractProfileDisplaySummary(loginBody, null, null).avatarSrc;
  if (fromLoginApi) {
    cacheProfileAvatarFromServer(fromLoginApi);
    return;
  }

  const picture = pictureFromGoogleCredential(credential);
  if (picture) {
    cacheProfileAvatarFromServer(picture);
  }
}

/**
 * Fill empty profile fields from Google OpenID claims and any phone returned by login.
 * Only patches fields that are still blank on GET /profile.
 */
export async function seedProfileFromGoogleLogin(credential: string, loginBody: unknown): Promise<void> {
  const google = googleProfileFromCredential(credential);
  syncProfileAvatarAfterGoogleLogin(credential, loginBody);

  if (!google.email && !google.firstName && !google.lastName && !google.picture) {
    return;
  }

  let profileData: unknown;
  try {
    profileData = await fetchProfile();
  } catch {
    return;
  }

  const phoneHint = contactHintsFromAuthBody(loginBody).phone;
  const summary = extractProfileDisplaySummary(
    profileData,
    google.fullName,
    google.email,
    getSessionToken(),
  );
  syncLocalBookingProfileFromSummary(summary, google.email);
  persistProfileContactHints(summary.email, summary.phone ?? undefined, google.email);
  const patch = buildProfilePatchFromGoogle(summary, google, phoneHint);

  if (!summary.avatarSrc && google.picture) {
    const uploadDataUrl = await fetchRemoteImageAsUploadDataUrl(google.picture);
    if (uploadDataUrl) {
      patch.profile_image = uploadDataUrl;
    } else {
      patch.profile_image = google.picture;
    }
  }

  if (Object.keys(patch).length === 0) {
    return;
  }

  try {
    const updated = await patchProfile(patch);
    const refreshed = extractProfileDisplaySummary(
      updated ?? profileData,
      google.fullName,
      google.email,
      getSessionToken(),
    );
    if (refreshed.avatarSrc && !loadLocalProfileAvatarSrc()) {
      cacheProfileAvatarFromServer(refreshed.avatarSrc);
    }
  } catch {
    /* local avatar + contact hints still improve the profile hub */
  }
}
